/* eslint-disable react/prop-types */

import PropTypes from 'prop-types';
import { createContext } from 'preact';
import classNames from 'classnames';
import { useEffect, useState, useMemo, useRef } from 'preact/hooks';
import { Transition } from 'react-transition-group';
import { useInView } from 'react-intersection-observer';

import { useTranslation } from 'react-i18next';
import { InteractionEvent, getAnalyticsContext } from '@playbook/sdk-helpers';

import styleSheet from './styles/teamSelector.module.scss';

import SelectorHeader from './components/Header.component';
import SelectorSponsor from './components/Sponsor.component';
import SelectorPitch from './components/SelectorPitch.component';
import Countdown from '../components/Countdown/Countdown.component';
import Hero from './components/Hero.component';

import {
    getAuthHeader,
    checkForSignIn,
    getUserIdFromAuthHeader
} from '../helpers/authentication';
import { sendView } from '../helpers/analytics';
import { getOrCreateEncodedUUID } from '../helpers/user-meta';

const styles = styleSheet.locals || {};

const THREE_SECONDS = 3000;

export const TeamSelectorContext = createContext();

/**
 * Component for showing a ScorePredictor
 *
 * @param {object} props -
 * @param {object} props.selectorData - prediction data
 * @param {object} props.embeddedFormData form data to embed after polling submit
 * @param {HTMLElement} props.container - embed container
 * @param {boolean} props.isPreview embed is a preview in admin UI
 * @param {boolean} props.colourOverrides - colour overrides
 * @param {Function} props.refetch - function to refetch data
 * @returns {Element} <Prediction />
 */
function TeamSelectorComponent({
    selectorData,
    // embeddedFormData,
    container,
    isPreview = false,
    colourOverrides,
    refetch
}) {
    const { t } = useTranslation();
    const [selector, setSelector] = useState(selectorData.selector);

    const [savedFormation, setSavedFormation] = useState(
        selectorData.selected_formation
    );

    const [savedPlayers, setSavedPlayers] = useState(
        selectorData.selected_players
    );

    const [startEmbedAnimation, setStartEmbedAnimation] = useState(false);

    const [isSignedIn, setIsSignedIn] = useState(false);
    const isGated = selector?.gated;
    const authHeader = getAuthHeader();

    // InteractionEvent for analytics
    const interactionEventRef = useRef(null);

    const transitionStyles = {
        entered: { opacity: 1, transform: 'rotate(0deg)' }
    };

    const isDraft =
        selector?.state === 'draft' || selector?.state === 'scheduled';

    const hasHeroImg =
        selector?.hero_image_url || selector?.hero_external_image_url;

    /* eslint-disable no-magic-numbers */
    const largePrediction =
        selector?.questions?.[0]?.options.length > 15 || hasHeroImg;

    const { ref } = useInView({
        triggerOnce: true,
        threshold: largePrediction ? 0.1 : 0.5,
        onChange: (inView) => {
            if (inView) {
                setStartEmbedAnimation(true);

                if (!isPreview) {
                    sendView(selector?.selector_id, isGated, authHeader)
                        .then(() => {
                            interactionEventRef.current?.send(
                                'TEAM_SELECTOR_VIEWED'
                            );
                        })
                        .catch((error) => {
                            // Handle the error here
                            console.error('Error sending view:', error);
                        });
                }
            }
        }
    });

    useEffect(() => {
        applyCustomSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setIsSignedIn(checkForSignIn());
    }, []);

    // Initialize InteractionEvent for analytics
    useEffect(() => {
        let userId = null;

        if (isGated && authHeader) {
            // For gated products: extract userId from JWT
            userId = getUserIdFromAuthHeader(authHeader);
        } else if (!isGated) {
            // For non-gated products: generate/retrieve from localStorage
            userId = getOrCreateEncodedUUID();
        }
        // Note: For gated products without auth, userId remains null

        const interactionEvent = new InteractionEvent({
            activationId: selector?.selector_id,
            userId,
            type: 'TEAM_SELECTOR',
            isLoggedIn: !!authHeader,
            title: selector?.title,
            context: getAnalyticsContext(container)
        });

        interactionEventRef.current = interactionEvent;

        // Expose globally for components without context access
        if (typeof globalThis !== 'undefined') {
            globalThis.PLAYBOOK_CONFIG = {
                ...globalThis.PLAYBOOK_CONFIG,
                interactionEvent
            };
        }
    }, [
        selector?.selector_id,
        selector?.title,
        isGated,
        authHeader,
        container
    ]);

    const applyCustomSettings = () => {
        const customSettingsAttr =
            container.previousElementSibling?.getAttribute('data-fek-settings');
        if (customSettingsAttr) {
            const settings = JSON.parse(customSettingsAttr);

            if (settings.primaryColour) {
                container.style.setProperty(
                    '--fek-primary',
                    settings.primaryColour
                );
            }

            if (settings.primaryColourRgb) {
                container.style.setProperty(
                    '--fek-primary-rgb',
                    settings.primaryColourRgb
                );
            }
        } else if (colourOverrides?.hex && colourOverrides?.rgb) {
            container?.style?.setProperty('--fek-primary', colourOverrides.hex);

            container?.style?.setProperty(
                '--fek-primary-rgb',
                colourOverrides.rgb
            );
        }
    };

    const onCountdownEnd = () => {
        setTimeout(async () => {
            // TODO: action this when working on closing state
            // https://pulselive.atlassian.net/browse/FEK-2169
        }, THREE_SECONDS);
    };

    const doRefetch = () => {
        if (refetch) {
            refetch();
        }
    };

    useEffect(() => {
        setSelector(selectorData.selector);
        setSavedFormation(selectorData.selected_formation);
        setSavedPlayers(selectorData.selected_players);
    }, [selectorData]);

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(
        () => ({
            selector,
            savedFormation,
            savedPlayers,
            setSavedPlayers,
            doRefetch,
            transitionStyles,
            isSignedIn,
            authHeader,
            isGated,
            interactionEvent: interactionEventRef.current
        }),
        [
            selector,
            savedFormation,
            savedPlayers,
            doRefetch,
            transitionStyles,
            isSignedIn,
            authHeader,
            isGated
        ]
    );

    const noPlayersSelected = savedPlayers.length === 0;

    const isClosed = selector?.state === 'closed';

    const hidePitch = isClosed && noPlayersSelected;

    // Do not render anything for draft or scheduled selectors
    if (isDraft) {
        return;
    }

    return (
        <TeamSelectorContext.Provider value={contextValue}>
            <style>{styleSheet.toString()}</style>
            <Transition nodeRef={ref} in={startEmbedAnimation}>
                {(transitionState) => (
                    <>
                        <div
                            data-cy="prediction-root"
                            ref={ref}
                            className={classNames(styles.teamSelector)}
                            part="ts-root"
                            style={{
                                maxWidth: selectorData.settings?.width || '',
                                ...transitionStyles[transitionState]
                            }}
                        >
                            {hasHeroImg && <Hero selector={selector} />}
                            <div
                                className={styles.teamSelectorContentWrapper}
                                part="ts-content"
                            >
                                <SelectorHeader />

                                {!hidePitch ? (
                                    <SelectorPitch
                                        transitionState={transitionState}
                                    />
                                ) : (
                                    <div
                                        className={
                                            styles.teamSelectorClosedText
                                        }
                                    >
                                        {t('selector.closedText')}
                                    </div>
                                )}

                                <Countdown
                                    text={t('selector.closesIn')}
                                    datetime={selector.closes_at}
                                    onCountdownEnd={onCountdownEnd}
                                    closes={true}
                                />
                                <SelectorSponsor />
                            </div>
                        </div>
                    </>
                )}
            </Transition>
        </TeamSelectorContext.Provider>
    );
}

TeamSelectorComponent.prototype = {
    selectorData: PropTypes.object.isRequired,
    embeddedFormData: PropTypes.object,
    container: PropTypes.node.isRequired,
    isPreview: PropTypes.bool,
    refetch: PropTypes.func
};

export default TeamSelectorComponent;
